import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { AdminGate } from "@/components/admin/AdminGate";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { api } from "@/lib/api";

type Prospect = {
  id: string;
  email: string;
  createdAt: string;
  brands: { id: string; name: string; asinCount: number; scrapedCount: number }[];
  latestInvite: { expiresAt: string; consumedAt: string | null; createdAt: string } | null;
  subscription: {
    status: string;
    tier: string;
    trialEndsAt: string | null;
    skuCap: number | null;
    scrapeCadence: string;
  } | null;
};

export default function AdminProspectList() {
  return (
    <AdminGate>
      <AdminLayout>
        <Inner />
      </AdminLayout>
    </AdminGate>
  );
}

function Inner() {
  const [prospects, setProspects] = useState<Prospect[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    api<{ prospects: Prospect[] }>("/admin/prospects").then((res) => {
      if (cancelled) return;
      setProspects(res.data?.prospects ?? []);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="mx-auto w-full max-w-6xl px-6 py-10">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Prospects</h1>
        <Link
          to="/admin/prospects/new"
          className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
        >
          + New prefill
        </Link>
      </div>

      {prospects === null && (
        <div className="mt-12 flex items-center gap-2 text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      )}

      {prospects && prospects.length === 0 && (
        <div className="mt-12 rounded-2xl border border-slate-200 bg-slate-50 p-8 text-center text-slate-600">
          No prospects yet. Create your first prefill to walk a customer through their dashboard.
        </div>
      )}

      {prospects && prospects.length > 0 && (
        <div className="mt-8 overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">Email</th>
                <th className="px-4 py-3 text-left">Brands</th>
                <th className="px-4 py-3 text-left">ASINs</th>
                <th className="px-4 py-3 text-left">Trial</th>
                <th className="px-4 py-3 text-left">Invite</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {prospects.map((p) => {
                const totalAsins = p.brands.reduce((s, b) => s + b.asinCount, 0);
                const totalScraped = p.brands.reduce((s, b) => s + b.scrapedCount, 0);
                const inviteState = p.latestInvite?.consumedAt
                  ? "consumed"
                  : p.latestInvite && new Date(p.latestInvite.expiresAt) > new Date()
                    ? "active"
                    : "expired";
                return (
                  <tr key={p.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium text-slate-900">{p.email}</td>
                    <td className="px-4 py-3 text-slate-700">
                      {p.brands.map((b) => b.name).join(", ") || "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      {totalScraped} / {totalAsins}
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      {p.subscription?.status === "trialing"
                        ? `Trialing · ends ${new Date(p.subscription.trialEndsAt ?? "").toLocaleDateString()}`
                        : p.subscription?.tier ?? "—"}
                    </td>
                    <td className="px-4 py-3">
                      <InvitePill state={inviteState} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={`/admin/customers/${p.id}`}
                        className="text-sm font-medium text-slate-900 hover:underline"
                      >
                        Open customer →
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function InvitePill({ state }: { state: "active" | "consumed" | "expired" }) {
  const map = {
    active: { cls: "bg-amber-50 text-amber-800 ring-amber-200", label: "Sent" },
    consumed: { cls: "bg-emerald-50 text-emerald-800 ring-emerald-200", label: "Used" },
    expired: { cls: "bg-slate-100 text-slate-600 ring-slate-200", label: "Expired" },
  } as const;
  const m = map[state];
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${m.cls}`}>
      {m.label}
    </span>
  );
}
